import type { CollabSession } from '../../state/catalogApi';
import { ChunkAssembler, closeAction, encodeBatch, type RtEvent } from './wire';

/** `rejoining`: a rejoin the web asked for on a live link (`resync`), no trouble to tell; a drop or a failed try is `reconnecting`. */
export type RtStatus = 'connecting' | 'live' | 'rejoining' | 'reconnecting';
/** How a link ends for good: until the person asks again (`superseded`, `full`, `unreachable`) or at all. */
export type RtConnectionTerminal = 'signed-out' | 'closed' | 'superseded' | 'full' | 'unreachable';

export interface RtClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}
export interface RtVisibility { visible(): boolean; subscribe(fn: () => void): () => void }
export interface RtConnectionDeps {
  /** A fresh session request, made right before each socket: the ticket it carries is one-use and lives 60 s. */
  fetchSession: (signal: AbortSignal) => Promise<CollabSession>;
  openSocket: (url: string) => WebSocket;
  clock: RtClock;
  visibility: RtVisibility;
}
type Events = { batch: RtEvent[]; status: RtStatus; terminal: RtConnectionTerminal };

export const browserClock: RtClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: handle => window.clearTimeout(handle as number),
  setInterval: (fn, ms) => window.setInterval(fn, ms),
  clearInterval: handle => window.clearInterval(handle as number),
};

export const pageVisibility: RtVisibility = {
  visible: () => document.visibilityState !== 'hidden',
  subscribe: fn => {
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  },
};

/** rt reaps a socket silent for 20 s (`sessionStore.js` zombie sweep). */
const PING_MS = 7500;
/** rt answers every Ping: silence this long, checked at each Ping (≈30 s after the last Pong, as the app's HeartbeatMonitor), is a link that dropped with no close. */
const SILENCE_MS = 25_000;
const BACKOFF_MS = 1000, BACKOFF_CAP_MS = 30_000;
/** Failed tries in a row that end in «could not connect»: before the first join, and after it (about five minutes at the backoff's cap). */
const FIRST_JOIN_TRIES = 3, RECONNECT_TRIES = 10;
/** A session request the catalog takes and never answers counts as a failed try after this long. */
const SESSION_DEADLINE_MS = 10_000;
/** A socket still not open after this long counts as a failed try (the app gives up on a join at 51 s). */
const OPEN_DEADLINE_MS = 15_000;
/** A re-sync this long after the last one starts its backoff over. */
const RESYNC_QUIET_MS = 120_000;

/**
 * The web's link to one rt room, a state machine (connecting → live ⇄ reconnecting, or a terminal end). It answers nothing
 * itself: it sends `Ping` and what its owner hands `send` — the web's own edits and, from `LiveSession`, the room's file
 * from a faithful copy only, so an empty or partial copy is never saved over the room.
 */
export class RtConnection {
  private listeners: { [K in keyof Events]: Set<(value: Events[K]) => void> } = { batch: new Set(), status: new Set(), terminal: new Set() };
  /** Bumped by each socket and by every stop: a late frame or close of an older socket reaches nobody. */
  private generation = 0;
  private socket: WebSocket | null = null;
  /** The room's state arrived on the current socket; a quick 4003 retry stays `live` for the person while its new socket is not in yet. */
  private inRoom = false;
  private request: AbortController | null = null;
  /** The one wait in flight: the backoff before the next try, or the deadline of a socket still opening. */
  private timer: unknown = null;
  private ping: unknown = null;
  private status: RtStatus | null = null;
  private ended = false;
  private disposed = false;
  private joined = false;
  private attempt = 0;
  private quickRetried = false;
  private failures = 0;
  private waitingVisible = false;
  private resyncs = 0;
  private lastResync = -Infinity;
  private stopVisibility: (() => void) | null = null;

  constructor(private readonly deps: RtConnectionDeps) {}

  on<K extends keyof Events>(name: K, fn: (value: Events[K]) => void): () => void {
    this.listeners[name].add(fn);
    return () => { this.listeners[name].delete(fn); };
  }

  start(): void {
    if (this.stopVisibility || this.disposed) return;
    this.stopVisibility = this.deps.visibility.subscribe(this.onVisibility);
    this.setStatus('connecting');
    void this.connect();
  }

  /** The person's own «reconnect»: after an end that waits for them, or at once instead of the next backoff step; `connecting` until its try fails. */
  retry(): void {
    if (this.disposed || !this.stopVisibility) return;
    this.ended = false;
    this.attempt = 0; this.failures = 0; this.quickRetried = false;
    this.setStatus('connecting');
    void this.connect();
  }

  /**
   * Rejoins for a fresh base under the shared backoff, so an old file's unknown ids never become a rejoin storm. One asked
   * `immediate` — the person's press, or rt's save of a room whose copy here is not faithful, one per save — rejoins at
   * once and leaves the backoff as it was: neither is a storm.
   */
  resync(immediate = false): void {
    if (this.disposed || this.ended) return;
    if (immediate) {
      this.drop(1000);
      this.setStatus(this.rejoining());
      void this.connect();
      return;
    }
    const now = this.deps.clock.now();
    if (now - this.lastResync > RESYNC_QUIET_MS) this.resyncs = 0;
    this.lastResync = now;
    this.drop(1000);
    this.setStatus(this.rejoining());
    this.later(this.backoff(this.resyncs++));
  }

  private rejoining(): RtStatus {
    return this.joined && !this.ended && this.status !== 'reconnecting' ? 'rejoining' : 'reconnecting';
  }

  close(): void {
    this.disposed = true;
    this.drop(1000);
    this.stopVisibility?.(); this.stopVisibility = null;
  }

  get canSend(): boolean { return this.inRoom && this.socket?.readyState === 1; }

  /** The web's own edit, one batch cut into Chunk parts as every frame is; false and nothing written unless in the room on an open socket — nothing is kept for later. */
  send(events: readonly RtEvent[]): boolean {
    const socket = this.socket;
    if (!this.inRoom || socket?.readyState !== 1) return false;
    for (const frame of encodeBatch(events)) socket.send(frame);
    return true;
  }

  private async connect(): Promise<void> {
    this.drop(1000);
    const generation = this.generation, request = this.request = new AbortController();
    const deadline = this.deps.clock.setTimeout(() => request.abort(), SESSION_DEADLINE_MS);
    let answer: CollabSession;
    try {
      answer = await this.deps.fetchSession(request.signal);
    } catch {
      if (generation === this.generation) this.failed();
      return;
    } finally {
      this.deps.clock.clearTimeout(deadline);
    }
    if (generation !== this.generation) return;
    this.request = null;
    if (answer.kind !== 'ok') { this.end(answer.kind); return; }
    if (!answer.ticket) { this.failed(); return; }
    const url = new URL(answer.url);
    url.searchParams.set('ticket', answer.ticket);
    url.searchParams.set('web', '1');
    const socket = this.socket = this.deps.openSocket(url.toString()), assembler = new ChunkAssembler();
    const mine = () => generation === this.generation;
    let heard = 0;
    this.timer = this.deps.clock.setTimeout(() => {
      this.timer = null;
      if (!mine()) return;
      this.drop(1000);
      this.failed();
    }, OPEN_DEADLINE_MS);
    socket.onopen = () => {
      if (!mine()) return;
      this.deps.clock.clearTimeout(this.timer); this.timer = null;
      heard = this.deps.clock.now();
      this.ping = this.deps.clock.setInterval(() => {
        if (!mine()) return;
        if (this.deps.clock.now() - heard >= SILENCE_MS) { this.closed(1006); socket.close(1000); return; }
        if (socket.readyState === 1) socket.send(encodeBatch([{ type: 'Ping' }])[0]);
      }, PING_MS);
    };
    socket.onmessage = event => {
      if (!mine()) return;
      heard = this.deps.clock.now();
      const events = assembler.feed(String(event.data));
      if (events) this.receive(events);
    };
    socket.onclose = event => { if (mine()) this.closed(event.code); };
  }

  private receive(events: RtEvent[]): void {
    if (events.some(event => event.type === 'SessionError' && event.code === 'SESSION_FULL')) { this.end('full'); return; }
    const joined = events.some(event => event.type === 'SessionState');
    if (joined) { this.joined = this.inRoom = true; this.attempt = 0; this.failures = 0; this.quickRetried = false; }
    const generation = this.generation;
    for (const fn of [...this.listeners.batch]) fn(events);
    // A listener that could not open the room's base has dropped the link already.
    if (joined && generation === this.generation) this.setStatus('live');
  }

  private closed(code: number): void {
    const dropped = this.inRoom;
    this.drop();
    const action = closeAction(code);
    if (action.kind === 'end') { this.end(action.terminal); return; }
    if (action.kind === 'new-ticket' && !this.quickRetried) {
      this.quickRetried = true;
      void this.connect();
      return;
    }
    this.failed(!dropped);
  }

  /** A try that got no room counts toward «could not connect»; the drop of a link that was in the room is no try. */
  private failed(counted = true): void {
    this.drop();
    if (counted && ++this.failures >= (this.joined ? RECONNECT_TRIES : FIRST_JOIN_TRIES)) { this.end('unreachable'); return; }
    this.setStatus(this.joined ? 'reconnecting' : 'connecting');
    this.later(this.backoff(this.attempt++));
  }

  private end(terminal: RtConnectionTerminal): void {
    this.drop(1000);
    this.ended = true;
    for (const fn of [...this.listeners.terminal]) fn(terminal);
  }

  /** 1→2→4…30 s, each stretched by 5–50 % at random, so a room's tabs do not come back in step. */
  private backoff(step: number): number {
    return Math.min(BACKOFF_CAP_MS, BACKOFF_MS * 2 ** step) * (1.05 + Math.random() * 0.45);
  }

  private later(ms: number): void {
    const generation = this.generation;
    this.timer = this.deps.clock.setTimeout(() => {
      this.timer = null;
      if (generation !== this.generation || this.disposed || this.ended) return;
      // A hidden tab keeps no reconnect loop: it waits to be shown.
      if (this.deps.visibility.visible()) void this.connect();
      else this.waitingVisible = true;
    }, ms);
  }

  private onVisibility = (): void => {
    if (!this.waitingVisible || !this.deps.visibility.visible() || this.disposed || this.ended) return;
    this.waitingVisible = false;
    void this.connect();
  };

  /** Lets go of the socket, its request and its timers; a close of our own is never read as the room's. */
  private drop(code?: number): void {
    this.generation++;
    this.inRoom = false;
    this.request?.abort(); this.request = null;
    if (this.timer !== null) { this.deps.clock.clearTimeout(this.timer); this.timer = null; }
    if (this.ping !== null) { this.deps.clock.clearInterval(this.ping); this.ping = null; }
    this.waitingVisible = false;
    const socket = this.socket;
    this.socket = null;
    if (socket && code !== undefined && socket.readyState < 2) socket.close(code);
  }

  private setStatus(status: RtStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const fn of [...this.listeners.status]) fn(status);
  }
}
